import React, { useEffect, useMemo, useState } from "react";
import { t } from "../i18n";
import "./MediaSelector.css";

function buildPickerUrl(target, returnPath, multi, type = "image") {
  const params = new URLSearchParams({
    picker: "1",
    target,
    type,
    multi: multi ? "1" : "0",
    return: returnPath
  });
  return `/media?${params.toString()}`;
}

export default function MediaSelector({
  title,
  selected = [],
  onChange,
  maxCount = 1,
  minCount = 0,
  accept = "image/*",
  allowUpload = true,
  allowLibrary = true,
  pickerTarget = "",
  pickerReturn = "",
  pickerMulti = true,
  gridLimit = 8
}) {
  const [mediaItems, setMediaItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let isMounted = true;
    async function loadMedia() {
      try {
        const res = await fetch("/api/fast/media/assets");
        if (!res.ok) throw new Error(t("mediasel.loadError"));
        const data = await res.json();
        const items = Array.isArray(data.items) ? data.items : [];
        const images = items
          .filter((item) => (item.type || "").toLowerCase() === "image" && item.status !== "deleted")
          .map((item) => ({
            url: item.url,
            thumbnail_url: item.thumbnail_url || "",
            mediaItemId: item.media_item_id || item.mediaItemId || "",
            mediaVersionId: item.version_id || item.mediaVersionId || "",
            assetId: item.asset_id || item.assetId || ""
          }));
        if (isMounted) setMediaItems(images);
      } catch {
        if (isMounted) setMediaItems([]);
      }
    }
    loadMedia();
    return () => {
      isMounted = false;
    };
  }, []);

  const mediaThumbs = useMemo(() => {
    const map = {};
    mediaItems.forEach((item) => {
      if (item.url && item.thumbnail_url) map[item.url] = item.thumbnail_url;
    });
    return map;
  }, [mediaItems]);

  const toggleSelection = (item) => {
    if (!item?.url) return;
    const exists = selected.find((entry) => entry.url === item.url);
    if (exists) {
      onChange(selected.filter((entry) => entry.url !== item.url));
      return;
    }
    if (maxCount === 1) {
      onChange([item]);
      return;
    }
    if (selected.length >= maxCount) return;
    onChange([...selected, item]);
  };

  async function handleUpload(event) {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setErrorMsg("");
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      const res = await fetch("/api/fast/media/uploads", {
        method: "POST",
        body: formData
      });
      if (!res.ok) throw new Error(t("mediasel.uploadError"));
      const data = await res.json();
      const newItems = Array.isArray(data.items) ? data.items : [];
      if (newItems.length) {
        setMediaItems((prev) => [...newItems, ...prev]);
      }
    } catch (err) {
      setErrorMsg(err.message || t("mediasel.uploadError"));
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  }

  const pickerUrl = allowLibrary && pickerTarget && pickerReturn
    ? buildPickerUrl(pickerTarget, pickerReturn, pickerMulti, "image")
    : "";

  const fileInputRef = React.useRef(null);

  return (
    <section className="nano-panel">
      <div className="nano-panel-header">
        <h2>{title}</h2>
      </div>
      <div className="nano-selected">
        {selected.map((entry) => (
          <div key={entry.url} className="nano-selected-wrap">
            <button
              type="button"
              className="nano-selected-item"
              onClick={() => toggleSelection(entry)}
            >
              <img src={mediaThumbs[entry.url] || entry.thumbnail_url || entry.url} alt="selection" />
            </button>
          </div>
        ))}
        {selected.length === 0 && (
          <div className="nano-muted">{t("mediasel.noSelection")}</div>
        )}
      </div>
      <div className="nano-muted">
        {selected.length} / {maxCount} {minCount > 0 ? `(min ${minCount})` : ""}
      </div>
      <div className="nano-selector-actions">
        {pickerUrl && (
          <a className="nano-selector-action-btn" href={pickerUrl}>
            <span className="material-symbols-rounded" style={{ fontSize: 16 }}>photo_library</span>
            {t("mediasel.library")}
          </a>
        )}
        {allowUpload && (
          <>
            <button
              type="button"
              className="nano-selector-action-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <span className="material-symbols-rounded" style={{ fontSize: 16 }}>
                {uploading ? "progress_activity" : "upload"}
              </span>
              {uploading ? "Upload..." : t("mediasel.upload") || "Upload"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={accept}
              onChange={handleUpload}
              style={{ display: "none" }}
            />
          </>
        )}
      </div>
      {errorMsg && <div className="nano-alert error">{errorMsg}</div>}
      <div className="nano-media-grid">
        {mediaItems.slice(0, gridLimit).map((item) => (
          <div key={item.url} className="nano-media-card-wrap">
            <button
              type="button"
              className={selected.some((entry) => entry.url === item.url) ? "nano-media-card selected" : "nano-media-card"}
              onClick={() => toggleSelection(item)}
            >
              <img src={item.thumbnail_url || item.url} alt="media" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
